import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, TrendingDown, ArrowRight, Settings as SettingsIcon, DollarSign,
  Receipt, Percent, ShoppingCart, Users, BarChart2, Check,
} from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { formatCurrency } from "../../lib/utils";

/**
 * Hero KPI — "Above-the-fold" emotional anchor of the Dashboard.
 *
 * Replaces the row of 8 stat-cards as the FIRST visual block. Shows one
 * big number (configurable by the user) + contextual greeting + tiny
 * sparkline + 3 secondary metrics as text + 2 CTAs.
 *
 * The user picks which metric to feature (today/week/month/avg_ticket/
 * avg_margin) via a settings menu — preference persisted in localStorage.
 *
 * Props:
 *   data — the same dashboard `data` object the parent receives from
 *          `getDashboardData()`. We only read: sales, top_products,
 *          sparklines, avg_margin, loyalty.
 */
const HERO_METRIC_KEY = "mg_dashboard_hero_metric";

const HERO_METRICS = [
  {
    id: "today",       icon: DollarSign,  label: "Ventas hoy",     sub: (s) => `${s.transactions_today || 0} tickets · ticket medio ${formatCurrency(s.avg_ticket || 0)}`,
    value: (s) => formatCurrency(s.today || 0),
    trend: (s) => s.today_trend,
  },
  {
    id: "week",        icon: BarChart2,   label: "Ventas esta semana",
    sub: (s) => `${s.transactions_week || 0} tickets`,
    value: (s) => formatCurrency(s.week || 0),
    trend: (s) => s.week_trend,
  },
  {
    id: "month",       icon: BarChart2,   label: "Ventas este mes",
    sub: (s) => `${s.transactions_month || 0} tickets`,
    value: (s) => formatCurrency(s.month || 0),
    trend: (s) => s.month_trend,
  },
  {
    id: "avg_ticket",  icon: Receipt,     label: "Ticket medio",
    sub: () => "Promedio del periodo",
    value: (s) => formatCurrency(s.avg_ticket || 0),
    trend: () => null,
  },
  {
    id: "avg_margin",  icon: Percent,     label: "Margen medio",
    sub: () => "% sobre coste de productos",
    value: (_s, d) => `${d?.avg_margin ?? 0}%`,
    trend: () => null,
  },
  {
    id: "customers",   icon: Users,       label: "Clientes totales",
    sub: (_s, d) => `${d?.loyalty?.total_points_issued ?? 0} puntos emitidos`,
    value: (_s, d) => String(d?.loyalty?.total_customers ?? 0),
    trend: () => null,
  },
];

// Time-based greeting (used as the small context line above the number).
function getGreeting(now = new Date()) {
  const h = now.getHours();
  const days = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
  const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  const date = `${days[now.getDay()]} ${now.getDate()} de ${months[now.getMonth()]}`;
  let shift = null;
  if (h >= 6 && h < 11)        shift = "Buenos días, preparando el servicio";
  else if (h >= 11 && h < 17)  shift = "Turno de comida activo";
  else if (h >= 17 && h < 20)  shift = "Tarde tranquila — preparando cena";
  else if (h >= 20 && h < 24)  shift = "Turno de cena activo";
  else                          shift = "Fuera de servicio";
  return { date, shift };
}

export default function HeroKpi({
  data,
  // Period selector — controlled from the parent so the same dropdown
  // drives the data fetch (Feb 2026: replaces the old filter row that
  // sat below the Hero and duplicated the same control).
  datePreset, setDatePreset,
  isCustom, setIsCustom,
  // Comparison vs previous period — surfaced as a small chip next to
  // the hero number. Provided by parent (already computed for the
  // legacy filter row).
  periodTrend,        // number or null — e.g. -8.3 means current period is 8.3% below
}) {
  const navigate = useNavigate();
  const [metricId, setMetricId] = useState(() => {
    try { return localStorage.getItem(HERO_METRIC_KEY) || "today"; }
    catch { return "today"; }
  });
  const [now, setNow] = useState(new Date());

  // Refresh greeting every minute so "preparando cena" → "Turno de cena"
  // transitions happen automatically without page reload.
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(tick);
  }, []);

  const setMetric = (id) => {
    setMetricId(id);
    try { localStorage.setItem(HERO_METRIC_KEY, id); } catch { /* ignore */ }
  };

  const metric = HERO_METRICS.find(m => m.id === metricId) || HERO_METRICS[0];
  const Icon = metric.icon;
  const sales = data?.sales || {};

  const value = metric.value(sales, data);
  const sub = metric.sub(sales, data);
  const trend = metric.trend(sales);

  const greeting = useMemo(() => getGreeting(now), [now]);

  // Sparkline: last 14 days of sales (always shows the trend backdrop, even
  // when the user is showing avg_margin etc. — visual context only)
  const sparkData = (data?.sparklines?.sales_7d || []).slice(-12);
  const sparkPath = useMemo(() => buildSparkPath(sparkData), [sparkData]);

  return (
    <Card
      data-testid="hero-kpi"
      className="relative overflow-hidden p-6 sm:p-8 border-none shadow-soft bg-gradient-to-br from-card via-card to-primary/5"
    >
      {/* ── Top row: greeting + settings ──────────────────────────── */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex flex-col gap-0.5 text-xs sm:text-sm">
          <span className="text-muted-foreground capitalize">{greeting.date}</span>
          <span className="text-foreground/80 font-medium">{greeting.shift}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Period selector — moved into the Hero so the user doesn't need
              a second filter row below. Same source of truth as before. */}
          <Select
            value={isCustom ? "custom" : datePreset}
            onValueChange={(v) => {
              if (v === "custom") setIsCustom(true);
              else { setIsCustom(false); setDatePreset(v); }
            }}
          >
            <SelectTrigger
              className="h-8 text-[11px] uppercase tracking-wide font-medium px-2.5 bg-muted/50 border-0 hover:bg-muted/80 transition w-auto gap-1"
              data-testid="hero-period-select"
              aria-label="Seleccionar periodo del KPI principal"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end" data-testid="hero-period-options">
              <SelectItem value="today">Hoy</SelectItem>
              <SelectItem value="week">Esta semana</SelectItem>
              <SelectItem value="month">Este mes</SelectItem>
              <SelectItem value="last_month">Mes anterior</SelectItem>
              <SelectItem value="year">Este año</SelectItem>
              <SelectItem value="year_prev">Año anterior</SelectItem>
              <SelectItem value="custom">Personalizado…</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost" size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                data-testid="hero-kpi-settings"
                aria-label="Cambiar métrica destacada"
              >
                <SettingsIcon className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Métrica destacada
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {HERO_METRICS.map(m => {
                const MIcon = m.icon;
                return (
                  <DropdownMenuItem
                    key={m.id}
                    onClick={() => setMetric(m.id)}
                    data-testid={`hero-kpi-pick-${m.id}`}
                    className="gap-2"
                  >
                    <MIcon className="w-4 h-4 text-muted-foreground" />
                    <span className="flex-1">{m.label}</span>
                    {m.id === metricId && <Check className="w-4 h-4 text-primary" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ── Hero number ───────────────────────────────────────────── */}
      <div className="flex items-start gap-3 mb-2">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
            {metric.label}
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tabular-nums leading-none"
              data-testid="hero-kpi-value"
            >
              {value}
            </div>
            {/* Period-vs-period comparison chip — replaces the legacy filter row */}
            {periodTrend !== null && periodTrend !== undefined && (
              <span
                className={`inline-flex items-center gap-1 text-sm font-semibold px-2.5 py-1 rounded-full ${
                  periodTrend >= 0
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                    : "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300"
                }`}
                data-testid="hero-kpi-period-trend"
                title="Comparado con el periodo anterior"
              >
                {periodTrend >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {periodTrend >= 0 ? "+" : ""}{periodTrend.toFixed(1)}%
                <span className="text-[11px] font-normal hidden sm:inline">vs periodo ant.</span>
              </span>
            )}
            {/* Fallback day-trend (kept for "today" metric where periodTrend may be null) */}
            {(periodTrend === null || periodTrend === undefined) && trend !== null && trend !== undefined && (
              <span
                className={`inline-flex items-center gap-1 text-sm font-semibold px-2 py-1 rounded-full ${
                  trend >= 0
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                    : "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300"
                }`}
                data-testid="hero-kpi-trend"
              >
                {trend >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {trend >= 0 ? "+" : ""}{trend}%
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">{sub}</p>
        </div>
      </div>

      {/* ── Sparkline (last 12 days) ──────────────────────────────── */}
      {sparkPath && (
        <svg
          viewBox="0 0 200 36"
          className="w-full h-9 mt-3 opacity-80"
          preserveAspectRatio="none"
          data-testid="hero-kpi-sparkline"
        >
          <defs>
            <linearGradient id="hero-spark-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={sparkPath.area} fill="url(#hero-spark-grad)" className="text-primary" />
          <path d={sparkPath.line} fill="none" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" className="text-primary" />
        </svg>
      )}

      {/* ── CTAs ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 mt-5">
        <Button
          size="sm" onClick={() => navigate("/app/tpv")}
          data-testid="hero-cta-tpv"
          className="gap-1.5"
        >
          <ShoppingCart className="w-4 h-4" />
          Abrir TPV
          <ArrowRight className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={() => navigate("/app/analytics?view=overview")}
          data-testid="hero-cta-analytics"
          className="gap-1.5"
        >
          <BarChart2 className="w-4 h-4" />
          Ver análisis
        </Button>
      </div>
    </Card>
  );
}

// Build the d=""attribute for the sparkline (line + area-fill paths).
function buildSparkPath(points) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const values = points.map(p => Number(p.value ?? p.total ?? p.amount ?? 0));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const stepX = 200 / (values.length - 1);
  const norm = (v) => 32 - ((v - min) / span) * 28 - 2;
  let line = "";
  values.forEach((v, i) => {
    const x = i * stepX;
    const y = norm(v);
    line += `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)} `;
  });
  const area = `${line} L ${(200).toFixed(2)} 36 L 0 36 Z`;
  return { line: line.trim(), area };
}
